import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Advertisement } from '../types';
import { CheckCircle, XCircle, Eye, AlertCircle, RotateCcw } from 'lucide-react';
import { NotificationModal } from './NotificationModal';
import { notificationService } from '../services/notificationService';
import { formatDescriptionHtml } from '../utils/formatDescription';

export const GlobalAdminApprovals: React.FC = () => {
  const { user, advertisements, partners, partnerAdmins, approveAdvertisement, rejectAdvertisement, changeAdvertisementStatus } = useAuth();
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  if (!user || user.role !== 'global_admin') {
    return null;
  }

  // Priority order mapping
  const priorityOrder: Record<string, number> = {
    'platinum': 1,
    'gold': 2,
    'silver': 3,
    'bronze': 4
  };

  // Approval status order mapping
  const statusOrder: Record<string, number> = {
    'pending': 1,
    'approved': 2,
    'rejected': 3
  };

  // Filter and sort advertisements
  // Sort by: 1) approval status, 2) priority level, 3) creation date (newest first)
  const filteredAds = advertisements
    .filter(ad => {
      const status = ad.approval_status || 'pending';
      if (filterStatus === 'all') return true;
      return status === filterStatus;
    })
    .sort((a, b) => {
      // First, sort by approval status (pending first)
      const statusA = a.approval_status || 'pending';
      const statusB = b.approval_status || 'pending';
      const statusComparison = (statusOrder[statusA] || 999) - (statusOrder[statusB] || 999);
      if (statusComparison !== 0) return statusComparison;

      // Second, sort by priority (platinum first)
      const priorityComparison = (priorityOrder[a.priority] || 999) - (priorityOrder[b.priority] || 999);
      if (priorityComparison !== 0) return priorityComparison;

      // Third, sort by creation date (newest first)
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA;
    });

  // Count by status (treat empty/NA as 'pending')
  const pendingCount = advertisements.filter(ad => !ad.approval_status || ad.approval_status === 'pending').length;
  const approvedCount = advertisements.filter(ad => ad.approval_status === 'approved').length;
  const rejectedCount = advertisements.filter(ad => ad.approval_status === 'rejected').length;

  const handleApprove = async (adId: string) => {
    try {
      await approveAdvertisement(adId, user.id);
      
      // Send tenant-wide notification to all partner admins
      const ad = advertisements.find(a => a.id === adId);
      
      if (ad) {
        try {
          await notificationService.createNotification({
            user_id: user.id, // Global admin who approved it (for tracking)
            type: 'ad_approved',
            title: 'Advertisement Approved ✅',
            message: `Your advertisement "${ad.name}" has been approved and is now live in campaigns.`,
            link: `/advertisements`,
            metadata: { ad_id: ad.id, ad_name: ad.name },
            priority: 'high',
            notification_scope: 'partner_tenant', // Tenant-wide for all partner admins
            partner_id: ad.partner_id // All admins of this partner will see it
          });
          console.log('✅ Tenant-wide approval notification sent to all admins of partner:', ad.partner_id);
        } catch (notifError) {
          console.error('Failed to send approval notification:', notifError);
          // Don't throw - notification failure shouldn't block ad approval
        }
      }
      
      setSelectedAd(null);
    } catch (error) {
      console.error('Approval error:', error);
      setNotification({
        isOpen: true,
        title: 'Approval Failed',
        message: error instanceof Error ? error.message : 'Failed to approve advertisement',
        type: 'error'
      });
    }
  };

  const handleReject = async () => {
    if (!selectedAd || !rejectionReason.trim()) {
      setNotification({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please provide a reason for rejection',
        type: 'warning'
      });
      return;
    }

    try {
      await rejectAdvertisement(selectedAd.id, user.id, rejectionReason);
      
      // Send tenant-wide notification to all partner admins
      try {
        await notificationService.createNotification({
          user_id: user.id, // Global admin who rejected it (for tracking)
          type: 'ad_rejected',
          title: 'Advertisement Rejected ❌',
          message: `Your advertisement "${selectedAd.name}" was rejected. Reason: ${rejectionReason}`,
          link: `/advertisements`,
          metadata: { ad_id: selectedAd.id, ad_name: selectedAd.name, rejection_reason: rejectionReason },
          priority: 'high',
          notification_scope: 'partner_tenant', // Tenant-wide for all partner admins
          partner_id: selectedAd.partner_id // All admins of this partner will see it
        });
        console.log('✅ Tenant-wide rejection notification sent to all admins of partner:', selectedAd.partner_id);
      } catch (notifError) {
        console.error('Failed to send rejection notification:', notifError);
        // Don't throw - notification failure shouldn't block ad rejection
      }
      
      setShowRejectModal(false);
      setSelectedAd(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Rejection error:', error);
      setNotification({
        isOpen: true,
        title: 'Rejection Failed',
        message: error instanceof Error ? error.message : 'Failed to reject advertisement',
        type: 'error'
      });
    }
  };

  const handleReturnToPending = async (adId: string) => {
    try {
      await changeAdvertisementStatus(adId, 'pending');
    } catch (error) {
      setNotification({
        isOpen: true,
        title: 'Status Change Failed',
        message: error instanceof Error ? error.message : 'Failed to return advertisement to pending',
        type: 'error'
      });
    }
  };

  const handleRejectFromApproved = async (adId: string) => {
    setSelectedAd(advertisements.find(ad => ad.id === adId) || null);
    setShowRejectModal(true);
  };

  const getPartnerName = (partnerId: string) => {
    return partners.find(p => p.id === partnerId)?.name || 'Unknown Partner';
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Advertisement Approvals</h2>
          <p className="text-gray-600 mt-1">Review and approve partner advertisements</p>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setFilterStatus('pending')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filterStatus === 'pending'
              ? 'border-yellow-500 text-yellow-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Pending ({pendingCount})
        </button>
        <button
          onClick={() => setFilterStatus('approved')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filterStatus === 'approved'
              ? 'border-green-500 text-green-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Approved ({approvedCount})
        </button>
        <button
          onClick={() => setFilterStatus('rejected')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filterStatus === 'rejected'
              ? 'border-red-500 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Rejected ({rejectedCount})
        </button>
        <button
          onClick={() => setFilterStatus('all')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filterStatus === 'all'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All ({advertisements.length})
        </button>
      </div>

      {/* Advertisements List */}
      <div className="grid gap-4">
        {filteredAds.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No advertisements found</p>
          </div>
        ) : (
          filteredAds.map(ad => (
            <div key={ad.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{ad.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(ad.approval_status || 'pending')}`}>
                      {(ad.approval_status || 'PENDING').toUpperCase()}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      {ad.priority.toUpperCase()}
                    </span>
                  </div>
                  
                  <div
                    className="text-gray-600 mb-3 max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 [&_li]:my-1 [&_p]:my-1"
                    dangerouslySetInnerHTML={{ __html: formatDescriptionHtml(ad.description) }}
                  />
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Partner:</span>
                      <span className="ml-2 font-medium text-gray-900">{getPartnerName(ad.partner_id)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Region:</span>
                      <span className="ml-2 font-medium text-gray-900">{ad.region}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Start Date:</span>
                      <span className="ml-2 font-medium text-gray-900">{new Date(ad.start_date).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">End Date:</span>
                      <span className="ml-2 font-medium text-gray-900">{new Date(ad.end_date).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Categories:</span>
                      <span className="ml-2 font-medium text-gray-900">{ad.categories.join(', ')}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-2 font-medium text-gray-900">{ad.status}</span>
                    </div>
                  </div>

                  {ad.rejection_reason && ad.approval_status === 'rejected' && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <p className="text-sm text-red-800">
                        <strong>Rejection Reason:</strong> {ad.rejection_reason}
                      </p>
                    </div>
                  )}

                  {ad.approved_at && (
                    <div className="mt-2 text-xs text-gray-500">
                      {ad.approval_status === 'approved' ? 'Approved' : 'Rejected'} on {new Date(ad.approved_at).toLocaleString()}
                    </div>
                  )}
                </div>

                {/* Public View Preview */}
                <div className="ml-6 w-64">
                  <div className="text-xs text-gray-500 mb-2 font-medium">Public View Preview:</div>
                  <div className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-gray-300">
                    {/* Desktop Layout Preview */}
                    <div className="p-1.5">
                      {/* Banner */}
                      <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-3 relative">
                        {ad.banner_url ? (
                          <img 
                            src={ad.banner_url} 
                            alt={ad.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                            <span className="text-purple-600 text-xs">No Image</span>
                          </div>
                        )}
                        
                        {/* Badge in top-right corner */}
                        {ad.badge_text && (
                          <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold shadow-lg ${
                            ad.badge_color === 'red' ? 'bg-red-500 text-white' :
                            ad.badge_color === 'blue' ? 'bg-blue-500 text-white' :
                            ad.badge_color === 'green' ? 'bg-green-500 text-white' :
                            ad.badge_color === 'yellow' ? 'bg-yellow-400 text-gray-900' :
                            ad.badge_color === 'purple' ? 'bg-purple-500 text-white' :
                            'bg-orange-500 text-white'
                          }`}>
                            {ad.badge_text}
                          </div>
                        )}
                      </div>
                      
                      {/* Advertisement Name */}
                      <h3 className="text-sm font-bold text-gray-900 text-center mb-3 px-1 line-clamp-2">{ad.name}</h3>
                      
                      {/* Button Preview */}
                      <button
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg font-medium text-xs cursor-default"
                      >
                        {ad.button_text || 'Get the Offer'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {(!ad.approval_status || ad.approval_status === 'pending') && (
                <div className="flex space-x-3 mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleApprove(ad.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedAd(ad);
                      setShowRejectModal(true);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                  <button
                    onClick={() => window.open(ad.banner_url, '_blank')}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Full Size</span>
                  </button>
                </div>
              )}

              {/* Action Buttons for Approved Ads */}
              {ad.approval_status === 'approved' && (
                <div className="flex space-x-3 mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleReturnToPending(ad.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Return to Pending</span>
                  </button>
                  <button
                    onClick={() => handleRejectFromApproved(ad.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                  <button
                    onClick={() => window.open(ad.banner_url, '_blank')}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Full Size</span>
                  </button>
                </div>
              )}

              {/* Action Buttons for Rejected Ads */}
              {ad.approval_status === 'rejected' && (
                <div className="flex space-x-3 mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleReturnToPending(ad.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Return to Pending</span>
                  </button>
                  <button
                    onClick={() => handleApprove(ad.id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => window.open(ad.banner_url, '_blank')}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Full Size</span>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Advertisement</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting "{selectedAd.name}"
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
            />
            <div className="flex space-x-3 mt-4">
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm Rejection
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedAd(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
      />
    </div>
  );
};
